import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDirection } from '../../../contexts/DirectionContext';
import { formatTimeAgo } from '../../../utils/date';
import { normalizeLanguageCode } from '../../../utils/platform';
import type { BaseMediaThumbnailProps } from '../../media/MediaThumbnail';
import type { Topic } from '../../../types/topic';
import HoverPlayButton from '../../common/HoverPlayButton';

interface TopicThumbnailProps extends Omit<BaseMediaThumbnailProps, 'aspectRatio' | 'navigationPath' | 'thumbnail'> {
  count: number;
  description?: string;
  status: Topic['status'];
  thumbnail?: string;
}

const TopicThumbnail: React.FC<TopicThumbnailProps> = ({
  id,
  title,
  thumbnail = '/images/topic-placeholder.jpg',
  createdAt,
  count,
  description,
  status,
  className = '',
}) => {
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const { t, i18n } = useTranslation();

  return (
    <div className={`group relative w-full ${className} ${isRTL ? 'rtl' : 'ltr'}`}>
      <Link 
        to={`/topics/${id}`}
        className="block w-full focus:outline-none"
      >
        <div className="relative w-full aspect-video rounded-xl overflow-hidden">
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60">
            <div className="absolute top-3 start-3">
              <Icon icon="ph:folder-thin" className="w-5 h-5 text-white" />
            </div>
          </div>

          {/* Thumbnail Image */}
          <img
            src={thumbnail}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            loading="lazy"
          />

          {/* Status Badge */}
          {status !== 'public' && (
            <div className="absolute top-3 end-3 px-2 py-1 rounded-lg bg-black/80 backdrop-blur-sm text-white text-xs font-medium z-20">
              {t(`common.${status}`)}
            </div>
          )}

          {/* Content Count */}
          <div className="absolute bottom-2 end-2 px-3 py-1 rounded-lg bg-black/80 backdrop-blur-sm text-white text-xs font-medium z-20">
            {count} {t(`common.items.${count === 1 ? 'one' : 'other'}`)}
          </div>

          <HoverPlayButton icon="ph:folder-simple-thin" />
        </div>

        <div className="mt-3">
          {/* Title */}
          <h3 className="font-medium text-text-primary line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
            {title}
          </h3>

          {/* Description */}
          {description && (
            <p className="mt-2 text-sm text-text-secondary line-clamp-2">
              {description}
            </p>
          )}
          
          {/* Metadata */}
          <div className="mt-1 flex items-center gap-2 text-sm text-text-secondary">
            <span>{count} {t(`common.items.${count === 1 ? 'one' : 'other'}`)}</span>
            <span>•</span>
            <time dateTime={createdAt.toString()}>
              {formatTimeAgo(createdAt, normalizeLanguageCode(i18n.language))}
            </time>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default TopicThumbnail; 